import { CheckCircle } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { UploadType } from 'further-types/files';
import FileDrop from 'further-ui/components/FileDrop';
import { Column, Row } from 'further-ui/components/Layout';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import { useFlag } from '@reflag/react-sdk';
import api from 'lib/trpcClient';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useAiFormFill } from '../AiFormFillContext';
import { useAiAgent } from 'contexts/AiAgentContext';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import { FormValues } from '../FormWrapper';
import { PendingInvoice } from 'components/AiAgent';

const InnerRow = styled(Row)(({ theme }) => ({
  padding: `0 ${theme.spacing(2)}`,
}));

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

type AiMediaType =
  | 'image/jpeg'
  | 'image/png'
  | 'image/gif'
  | 'image/webp'
  | 'application/pdf';

const getAiMediaType = (file: File): AiMediaType | null => {
  const { type } = file;
  if (
    type === 'image/jpeg' ||
    type === 'image/png' ||
    type === 'image/gif' ||
    type === 'image/webp' ||
    type === 'application/pdf'
  ) {
    return type;
  }
  return null;
};

type Props = {
  vehicleId: string;
};

const InvoiceUploadSection: FC<Props> = ({ vehicleId }) => {
  const { control, watch, setValue } = useFormContext<FormValues>();
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();
  const { isEnabled: showAiAgent } = useFlag('show-experimental-ai-agent');
  const [aiScanComplete, setAiScanComplete] = useState(false);
  const {
    applyFormFill,
    hints,
    isUploadingInvoice,
    setIsUploadingInvoice,
    isAiScanning,
    setIsAiScanning,
  } = useAiFormFill();

  const { pushNotification } = useAiAgent();
  const chatMutation = api.aiAgent.chatFormFill.useMutation();
  const location = useLocation<{ pendingInvoice?: PendingInvoice }>();
  const initialPendingInvoice = useRef(location.state?.pendingInvoice);

  type FormFillData = NonNullable<
    Awaited<ReturnType<typeof chatMutation.mutateAsync>>['formFill']
  >;

  const buildNotification = (formFill: FormFillData): string => {
    const isPartial = Object.values(formFill).some(
      (field) => field && field.confidence !== 'high',
    );
    const category = formFill.category?.value;
    const categoryLabel =
      category === 'income'
        ? 'Income'
        : category === 'expense'
        ? 'Expense'
        : null;
    const state = isPartial ? 'partially populated' : 'populated';
    return categoryLabel
      ? `${categoryLabel} fields have been ${state}`
      : `Fields have been ${state}`;
  };

  useEffect(() => {
    const pending = initialPendingInvoice.current;
    if (!pending || !showAiAgent) return;

    setValue('invoiceFile', {
      path: pending.uploadedPath,
      name: pending.uploadedName,
      preview: pending.uploadedPreview,
    });

    setIsAiScanning(true);
    setAiScanComplete(false);

    chatMutation
      .mutateAsync({
        vehicleId,
        file: {
          base64: pending.base64,
          mediaType: pending.mediaType,
          fileName: pending.fileName,
        },
      })
      .then((response) => {
        if (response.formFill) {
          applyFormFill(response.formFill);
          pushNotification(buildNotification(response.formFill));
        }
        setAiScanComplete(true);
      })
      .catch(() => {
        // AI scan failure is non-critical
      })
      .finally(() => {
        setIsAiScanning(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasLowConfidenceFields = Object.values(hints).some(
    (hint) => hint && hint.confidence !== 'high',
  );

  const invoiceFile = watch('invoiceFile');

  const handleInvoiceFileDrop = async (
    files: Array<{ fileName: string; previewUri: string; fileData: File }>,
    onChange: (
      value: { path: string; name: string; preview: string } | null,
    ) => void,
  ) => {
    const rawFile = files?.[0]?.fileData;
    if (!rawFile) return;

    setIsUploadingInvoice(true);
    setAiScanComplete(false);

    try {
      const mediaType = showAiAgent ? getAiMediaType(rawFile) : null;
      const [uploadResult, base64] = await Promise.all([
        uploadFile(rawFile, UploadType.LpTransactionAttachments),
        mediaType ? fileToBase64(rawFile) : Promise.resolve(null),
      ]);

      if (uploadResult.data) {
        onChange({
          path: uploadResult.data.filePath,
          name: uploadResult.data.name ?? rawFile.name,
          preview: uploadResult.data.url,
        });
      } else {
        error(uploadResult.error.message);
        return;
      }

      if (showAiAgent && base64 && mediaType) {
        setIsUploadingInvoice(false);
        setIsAiScanning(true);
        try {
          const response = await chatMutation.mutateAsync({
            vehicleId,
            file: { base64, mediaType, fileName: rawFile.name },
          });
          if (response.formFill) {
            applyFormFill(response.formFill);
            pushNotification(buildNotification(response.formFill));
          }
          setAiScanComplete(true);
        } catch {
          // AI scan failure is non-critical
        } finally {
          setIsAiScanning(false);
        }
      }
    } finally {
      setIsUploadingInvoice(false);
    }
  };

  return (
    <Controller
      name="invoiceFile"
      control={control}
      render={({ field: { onChange } }) => (
        <FileDrop
          inputTitle="Upload invoice"
          maxFiles={1}
          spacing={4}
          hideDropzoneWhenPopulated
          files={
            invoiceFile
              ? [
                  {
                    fileName: invoiceFile.name,
                    previewUri: invoiceFile.preview,
                  },
                ]
              : isUploadingInvoice
              ? [{ fileName: '', isUploading: true }]
              : []
          }
          onDeleteFile={() => {
            onChange(null);
            setAiScanComplete(false);
          }}
          onDropFile={(files) => handleInvoiceFileDrop(files, onChange)}
        >
          {showAiAgent && (isAiScanning || aiScanComplete) ? (
            <InnerRow justify="space-between" centered>
              <Row centered spacing="sm">
                <GradientAiIcon size={20} style={{ flexShrink: 0 }} />
                {isAiScanning ? (
                  <Typography variant="body2" color="text.secondary">
                    Scanning upload...
                  </Typography>
                ) : (
                  <Column>
                    <Typography variant="h6">
                      {hasLowConfidenceFields
                        ? 'Fields have been partially populated'
                        : 'Fields have been populated'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasLowConfidenceFields
                        ? 'Please input the remaining fields and submit.'
                        : 'Feel free to check the below and submit.'}
                    </Typography>
                  </Column>
                )}
              </Row>
              {isAiScanning ? (
                <CircularProgress size={18} sx={{ flexShrink: 0 }} />
              ) : (
                <CheckCircle
                  sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }}
                />
              )}
            </InnerRow>
          ) : null}
        </FileDrop>
      )}
    />
  );
};

export default InvoiceUploadSection;
